<script lang="ts" setup>
import { computed } from 'vue';
import { CdrText, CdrAccordionGroup, CdrLink } from '@rei/cedar';
import ResourceSubSection from '@returns-page/components/resource-section/components/ResourceSubSection.vue';
import Formatters from '@returns-page/utils/formatters.mts';
import type { ResourceSectionProps } from '@returns-page/types/index.mts';
import DOMTools from '@utils/dom-tools.mts';

const props = defineProps<ResourceSectionProps>();

const subSections = computed(() => (
  (props.resource?.resourceHubSubsections || []).map(
    (section) => ({
      ...section,
      sectionId: Formatters.textToID(section.heading, { slashesToDash: true }),
      title: section.heading,
      body: DOMTools.richTextToHtml(section.body, { sanitize: true }),
    }),
  )
));
</script>

<template>
  <div
    :id="props.resource.sectionId"
    :data-index="props.index"
    class="resource-section"
    data-ui="resource-section-component"
  >
    <cdr-text
      class="resource-section__title"
      data-ui="resource-section-title"
      tag="h2"
    >
      {{ props.resource.title.replace('-', '/') }}
    </cdr-text>
    <div class="resource-section__body">
      <div
        data-ui="resource-section-body"
        v-html="props.resource.body"
      />
      <div
        v-if="subSections.length"
        class="resource-section__body__subsection"
        data-ui="resource-section-subsections"
      >
        <cdr-accordion-group>
          <resource-sub-section
            v-for="(subSection, subIndex) in subSections"
            :key="`${subSection.title}-${subIndex}`"
            :index="subIndex"
            v-bind="subSection"
          />
        </cdr-accordion-group>
      </div>
    </div>
    <cdr-link
      class="return-to-nav"
      aria-label="Return to Returns and Exchanges page navigation"
      href="#in-page-nav"
    >
      Return to Returns & Exchanges page navigation
    </cdr-link>
  </div>
</template>

<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';
@import '@rei/cdr-component-variables/dist/scss/index.scss';

.resource-section {
  padding: 0 $cdr-space-one-x;

  & &__title {
    @include cdr-text-heading-serif-700;

    margin: $cdr-space-two-x 0;
  }

  &__body {
    h3 {
      @include cdr-text-subheading-sans-500;
    }

    h4 {
      @include cdr-text-subheading-sans-400;

      margin-bottom: $cdr-space-half-x;
    }

    p {
      @include cdr-text-body-300;

      margin-bottom: $cdr-space-one-x;
    }

    ul {
      @include cdr-list-base-mixin;
      @include cdr-list-unordered-mixin;

      margin-bottom: $cdr-space-one-x;
    }

    & .btn-container {
      @include cdr-xs-mq-up {
        display: flex;
        flex-direction: column;
        gap: $cdr-space-one-x;
        width: fit-content;
      }

      @include cdr-md-mq-up {
        flex-direction: unset;
        width: unset;
      }
    }

    &__subsection {
      margin: $cdr-space-one-x 0;
    }
  }
}

strong {
  font-weight: 600;
}

// hides link so it is not visible but keyboard users can select it
// to return to the nav
.return-to-nav {
  clip-path: circle(0) !important;
}

.return-to-nav:focus {
  padding: $cdr-space-half-x;
  text-transform: uppercase;
  text-decoration: none;
  clip-path: none !important;
  color: $cdr-color-border-tab-keyline-active;
}

.no-wrap {
  white-space: nowrap;
}

.resource-section__body {
  table {
    display: table;
    width: 100%;
    margin-bottom: $cdr-space-one-x;
    border-spacing: 0;
    border-collapse: collapse;
    border: 1px solid $cdr-color-border-primary;
    border-top: unset;

    th,
    td {
      padding: $cdr-space-one-x;
    }

    thead {
      border: 1px solid $cdr-color-border-primary;
      border-bottom: unset;
      text-align: left;
      color: $cdr-color-text-emphasis;
      background: #acb4a2;

      tr {
        background: #acb4a2;
      }

      p {
        @include cdr-text-utility-sans-strong-300;

        margin: 0;
      }
    }

    tbody {
      tr:first-child > td > p {
        @include cdr-text-utility-sans-strong-200;

        text-align: left;
      }

      tr:nth-child(odd):first-child {
        background: #f4f2ed;
      }

      tr:nth-child(odd) {
        background: #f9f8f6;
      }

      tr:nth-child(even) {
        background: #fff;
      }

      p {
        margin: 0;
      }

      & tr > td:first-child p {
        @include cdr-text-utility-sans-strong-200;

        @include cdr-sm-mq-down {
          max-width: 200px;
        }
      }

      & tr a {
        @include cdr-text-utility-sans-200;
      }

      & tr > td p {
        @include cdr-text-utility-sans-200;
      }

      & tr:nth-of-type(2) td p:first-child {
        margin-bottom: $cdr-space-eighth-x;
      }
    }
  }
}
</style>
